import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { StatusBadge, fmtEUR } from "@/components/StatusBadge";
import { useAuth } from "@/lib/auth";
import { apiClient, readError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Briefcase, Banknote, ShieldCheck, Star, Inbox, Layers, Crown, Sparkles, FileText, Download, BadgeCheck } from "lucide-react";
import SiretVerifier from "@/components/SiretVerifier";
import BankDetailsForm from "@/components/BankDetailsForm";
import InsuranceForm from "@/components/InsuranceForm";
import ClientInviteLink from "@/components/ClientInviteLink";
import GoogleMapsCard from "@/components/GoogleMapsCard";
import DonneurOrdreBoard from "@/components/DonneurOrdreBoard";
import ConversationsCard from "@/components/ConversationsCard";
import ArtisanOrganizer from "@/components/ArtisanOrganizer";
import ArtisanAgenda from "@/components/ArtisanAgenda";
import DemandeurProfileChecklist from "@/components/DemandeurProfileChecklist";
import MyArtisansCard from "@/components/MyArtisansCard";
import ExcelCard from "@/components/ExcelCard";
import PushOnboardingBanner from "@/components/PushOnboardingBanner";

const DEMANDEUR_TYPE_LABEL = {
  particulier: "Particulier",
  architecte: "Architecte",
  promoteur_immobilier: "Promoteur immo.",
  cmi: "Constructeur (CMI)",
  syndic: "Syndic",
  bailleur: "Bailleur",
  entreprise_btp: "Entreprise BTP",
  collectivite: "Collectivité",
  maitre_oeuvre: "Maître d'œuvre",
  investisseur: "Investisseur",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [premium, setPremium] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showSiretForm, setShowSiretForm] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [mRes, cRes, iRes] = await Promise.all([
          apiClient.get("/missions"),
          apiClient.get("/chantiers"),
          apiClient.get("/invoices/me"),
        ]);
        setMissions(mRes.data.missions || []);
        setChantiers(cRes.data.chantiers || []);
        setInvoices(iRes.data.invoices || []);
        if (user?.role === "artisan") {
          const p = await apiClient.get("/premium/status");
          setPremium(p.data);
        }
      } catch (e) {
        toast.error(readError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const isDemandeur = user?.role === "demandeur";
  const isArtisan = user?.role === "artisan";

  const stats = {
    total: missions.length,
    active: missions.filter((m) => ["paid", "quoted", "open"].includes(m.status)).length,
    completed: missions.filter((m) => m.status === "completed").length,
    escrow: missions
      .filter((m) => m.status === "paid")
      .reduce((s, m) => s + (m.accepted_amount || 0), 0),
  };

  // Standalone missions = missions without a chantier_id
  const standaloneMissions = missions.filter((m) => !m.chantier_id);

  return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <PushOnboardingBanner />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
              // Tableau de bord · {user?.role}
              {isDemandeur && user?.demandeur_type && user.demandeur_type !== "particulier" && (
                <span className="badge-pill bg-[#0A0A0B] text-white border-[#0A0A0B] mono text-[10px] uppercase tracking-widest" data-testid="dashboard-demandeur-type">
                  {DEMANDEUR_TYPE_LABEL[user.demandeur_type] || user.demandeur_type}
                </span>
              )}
            </div>
            <h1 className="display text-5xl mt-2">Bonjour, {user?.name}.</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            {isDemandeur && (
              <>
                <Link to="/chantiers/new" data-testid="new-chantier-btn">
                  <button className="btn-anthracite flex items-center gap-2">
                    <Layers className="w-4 h-4" /> Nouveau chantier
                  </button>
                </Link>
                <Link to="/missions/new" data-testid="new-mission-btn">
                  <button className="btn-batisur flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Mission simple
                  </button>
                </Link>
              </>
            )}
            {isArtisan && (
              <Link to="/artisans" data-testid="see-mks">
                <button className="btn-anthracite">Voir l'annuaire</button>
              </Link>
            )}
          </div>
        </div>

        {/* SIRET banner for unverified artisans */}
        {isArtisan && user && !user.siret_verified && (
          <div className="mb-8">
            <div className="border-2 border-amber-400 bg-amber-50 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="siret-banner">
              <div className="flex items-center gap-3">
                <BadgeCheck className="w-7 h-7 text-amber-700" />
                <div>
                  <div className="display text-xl text-amber-900">Vérifiez votre SIRET</div>
                  <div className="text-sm text-amber-800 mt-1">
                    Obligatoire pour les devis ≥ <strong>5 000 €</strong>. Gain de confiance immédiat
                    pour les particuliers.
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowSiretForm((s) => !s)}
                className="btn-batisur text-xs flex items-center gap-2"
                data-testid="open-siret-verifier"
              >
                <BadgeCheck className="w-4 h-4" /> {showSiretForm ? "Fermer" : "Vérifier mon SIRET"}
              </button>
            </div>
            {showSiretForm && (
              <div className="mt-3">
                <SiretVerifier
                  initial={user.siret || ""}
                  onVerified={() => {
                    window.location.reload();
                  }}
                />
              </div>
            )}
          </div>
        )}

        {isArtisan && user?.siret_verified && (
          <div className="mb-8 border-2 border-emerald-700 bg-emerald-50 p-4 flex items-center gap-3" data-testid="siret-verified-banner">
            <ShieldCheck className="w-6 h-6 text-emerald-700" />
            <div className="flex-1">
              <div className="display text-lg text-emerald-900">SIRET vérifié — {user.siret_denomination || user.siret}</div>
              <div className="mono text-[10px] uppercase tracking-widest text-emerald-800 mt-0.5">
                {user.siret_naf || "Activité enregistrée"} · badge officiel actif sur votre profil
              </div>
            </div>
          </div>
        )}

        {/* Bank details (IBAN/BIC) — artisan only */}
        {isArtisan && <ClientInviteLink />}
        {isArtisan && <ArtisanOrganizer />}
        {isArtisan && <ArtisanAgenda />}
        <ConversationsCard />
        {isArtisan && <BankDetailsForm />}
        {isArtisan && <InsuranceForm />}
        {isArtisan && <GoogleMapsCard />}

        {/* Donneur d'ordre : vue consolidée + carnet d'artisans + import/export Excel */}
        {isDemandeur && <DonneurOrdreBoard />}
        {isDemandeur && <MyArtisansCard />}
        {isDemandeur && <ExcelCard />}
        {isDemandeur && missions.some((m) => m.mo_fee_type) && <BankDetailsForm />}

        {/* Onboarding profile checklist — demandeur only */}
        {isDemandeur && <DemandeurProfileChecklist />}

        {/* Premium banner for artisans */}
        {isArtisan && premium && (
          <Link to="/pro/premium" className="block mb-8">
            {premium.is_gold ? (
              <div className="border-2 border-amber-600 bg-amber-50 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="gold-banner-active">
                <div className="flex items-center gap-3">
                  <Crown className="w-6 h-6 text-amber-700" />
                  <div>
                    <div className="display text-xl text-amber-900">Pack Or actif</div>
                    <div className="text-xs text-amber-800 mono uppercase tracking-widest mt-1">
                      Jusqu'au {new Date(premium.gold_until).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-amber-800 font-semibold">Gérer mon abonnement →</div>
              </div>
            ) : premium.is_premium && user?.welcome_trial_granted ? (
              (() => {
                const daysLeft = premium.premium_until
                  ? Math.max(0, Math.ceil((new Date(premium.premium_until) - new Date()) / 86400000))
                  : 0;
                return (
                  <div className="border-2 border-[#FF4D00] bg-gradient-to-r from-[#FFF3E5] to-white p-5 flex flex-wrap items-center justify-between gap-4" data-testid="welcome-trial-banner">
                    <div className="flex items-center gap-3">
                      <Sparkles className="w-6 h-6 text-[#FF4D00]" />
                      <div>
                        <div className="display text-xl text-[#0A0A0B]">🎁 Essai Pack Pro — {daysLeft} jour{daysLeft > 1 ? "s" : ""} restant{daysLeft > 1 ? "s" : ""}</div>
                        <div className="text-xs text-zinc-700 mt-1">
                          Votre profil est en tête d'annuaire jusqu'au {new Date(premium.premium_until).toLocaleDateString("fr-FR")}.
                          Continuez ensuite à 29€/mois sans engagement.
                        </div>
                      </div>
                    </div>
                    <div className="btn-batisur">Voir mes avantages →</div>
                  </div>
                );
              })()
            ) : premium.is_premium ? (
              <div className="border-2 border-emerald-700 bg-emerald-50 p-5 flex flex-wrap items-center justify-between gap-4" data-testid="premium-banner-active">
                <div className="flex items-center gap-3">
                  <Crown className="w-6 h-6 text-emerald-700" />
                  <div>
                    <div className="display text-xl text-emerald-900">Premium actif</div>
                    <div className="text-xs text-emerald-800 mono uppercase tracking-widest mt-1">
                      Jusqu'au {new Date(premium.premium_until).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                </div>
                <div className="text-sm text-emerald-800 font-semibold">Passer Or ou gérer →</div>
              </div>
            ) : (
              <div className="border-2 border-[#FF4D00] bg-[#0A0A0B] text-white p-5 flex flex-wrap items-center justify-between gap-4 lift" data-testid="premium-banner-inactive">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-[#FF4D00]" />
                  <div>
                    <div className="display text-xl">Passez Premium · {premium.premium_price ?? 29}€ / {premium.premium_days ?? 30} jours</div>
                    <div className="text-xs text-zinc-400 mt-1">
                      Ou Pack Or · {premium.gold_price ?? 49}€ pour les artisans vérifiés (SIRET + {premium.gold_min_completed ?? 10} missions)
                    </div>
                  </div>
                </div>
                <div className="btn-batisur">Découvrir →</div>
              </div>
            )}
          </Link>
        )}

        {/* STATS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-[#0A0A0B] bg-white mb-10">
          <StatCard icon={Briefcase} label="Missions" value={stats.total} />
          <StatCard icon={Inbox} label="En cours" value={stats.active} accent />
          <StatCard icon={ShieldCheck} label="Sous séquestre" value={fmtEUR(stats.escrow)} />
          <StatCard icon={Star} label="Terminées" value={stats.completed} />
        </div>

        {/* CHANTIERS LIST */}
        {chantiers.length > 0 && (
          <div className="border-2 border-[#0A0A0B] bg-white mb-10">
            <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center justify-between">
              <div className="display text-2xl flex items-center gap-2">
                <Layers className="w-5 h-5 text-[#FF4D00]" /> Mes chantiers
              </div>
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">{chantiers.length}</div>
            </div>
            {chantiers.map((c) => (
              <Link
                key={c.id}
                to={`/chantiers/${c.id}`}
                className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 transition"
                data-testid={`chantier-row-${c.id}`}
              >
                <div className="col-span-12 lg:col-span-6">
                  <div className="display text-xl">{c.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {c.city} · {c.lots_count} lot{c.lots_count > 1 ? "s" : ""}
                    {c.deadline && ` · livraison ${c.deadline}`}
                  </div>
                </div>
                <div className="col-span-6 lg:col-span-2 flex items-center mono text-sm font-semibold">
                  {fmtEUR(c.total_amount || 0)}
                </div>
                <div className="col-span-6 lg:col-span-3 flex items-center gap-2">
                  {c.status_summary?.paid > 0 && (
                    <span className="badge-pill bg-[#FF4D00] text-white border-[#FF4D00]">
                      {c.status_summary.paid} séquestré
                    </span>
                  )}
                  {c.status_summary?.completed > 0 && (
                    <span className="badge-pill bg-emerald-600 text-white border-emerald-700">
                      {c.status_summary.completed} terminé
                    </span>
                  )}
                </div>
                <div className="col-span-12 lg:col-span-1 flex items-center justify-end mono text-[11px] uppercase tracking-widest text-zinc-500">
                  Voir →
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* INVOICES (current user) */}
        {invoices.length > 0 && (
          <div className="border-2 border-[#0A0A0B] bg-white mb-10">
            <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center justify-between">
              <div className="display text-2xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#FF4D00]" /> Mes factures
              </div>
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
                {invoices.length}
              </div>
            </div>
            {invoices.slice(0, 5).map((inv) => (
              <div
                key={inv.id}
                className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 transition items-center"
                data-testid={`invoice-row-${inv.id}`}
              >
                <div className="col-span-12 lg:col-span-3 mono text-sm font-semibold">
                  {inv.number}
                </div>
                <div className="col-span-6 lg:col-span-4 text-sm text-zinc-700">
                  {inv.items?.[0]?.description || inv.title}
                </div>
                <div className="col-span-6 lg:col-span-2 mono text-xs uppercase tracking-widest text-zinc-500">
                  {(inv.issued_at || "").slice(0, 10)}
                </div>
                <div className="col-span-6 lg:col-span-2 display text-base">
                  {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(inv.total_ttc || 0)}
                </div>
                <div className="col-span-6 lg:col-span-1 flex justify-end">
                  <a
                    href={`${BACKEND_URL}/api/invoices/${inv.id}/pdf`}
                    download={`${inv.number}.pdf`}
                    className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest border-2 border-[#0A0A0B] px-2 py-1.5 hover:bg-[#0A0A0B] hover:text-white"
                    data-testid={`download-invoice-${inv.id}`}
                  >
                    <Download className="w-3 h-3" /> Télécharger
                  </a>
                </div>
              </div>
            ))}
            {invoices.length > 5 && (
              <div className="px-6 py-3 mono text-[11px] uppercase tracking-widest text-zinc-500 border-t border-zinc-200">
                + {invoices.length - 5} autre(s) facture(s)
              </div>
            )}
          </div>
        )}

        {/* MISSIONS LIST */}
        <div className="border-2 border-[#0A0A0B] bg-white">
          <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex items-center justify-between">
            <div className="display text-2xl">
              {isDemandeur
                ? "Missions indépendantes"
                : isArtisan
                ? "Missions disponibles & en cours"
                : "Missions"}
            </div>
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
              {(isDemandeur ? standaloneMissions : missions).length} entrée(s)
            </div>
          </div>

          {loading && <div className="p-10 mono text-sm">Chargement…</div>}
          {!loading && (isDemandeur ? standaloneMissions : missions).length === 0 && (
            <div className="p-10 text-center">
              <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">// Vide</div>
              <div className="display text-2xl mt-2">
                {isDemandeur ? "Aucune mission indépendante" : "Aucune mission pour le moment"}
              </div>
              {isDemandeur && chantiers.length === 0 && (
                <div className="flex gap-3 justify-center mt-6">
                  <Link to="/chantiers/new">
                    <button className="btn-anthracite">Lancer un chantier complet</button>
                  </Link>
                  <Link to="/missions/new">
                    <button className="btn-batisur">Publier une mission simple</button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {!loading &&
            (isDemandeur ? standaloneMissions : missions).map((m) => (
              <Link
                key={m.id}
                to={`/missions/${m.id}`}
                className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50 transition"
                data-testid={`mission-row-${m.id}`}
              >
                <div className="col-span-12 lg:col-span-6">
                  <div className="display text-xl">{m.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {m.category} · {m.city}
                    {m.chantier_id && (
                      <span className="ml-2 mono uppercase tracking-widest text-[#FF4D00]">
                        · Lot de chantier
                      </span>
                    )}
                    {m.deadline && ` · échéance ${m.deadline}`}
                  </div>
                </div>
                <div className="col-span-6 lg:col-span-2 flex items-center">
                  <div className="mono text-sm font-semibold">
                    {m.accepted_amount
                      ? fmtEUR(m.accepted_amount)
                      : `${fmtEUR(m.budget_min)} – ${fmtEUR(m.budget_max)}`}
                  </div>
                </div>
                <div className="col-span-6 lg:col-span-2 flex items-center">
                  <StatusBadge status={m.status} />
                </div>
                <div className="col-span-12 lg:col-span-2 flex items-center justify-end mono text-[11px] uppercase tracking-widest text-zinc-500">
                  Voir →
                </div>
              </Link>
            ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-6 border-r-2 last:border-r-0 border-[#0A0A0B] ${accent ? "bg-[#0A0A0B] text-white" : ""}`}>
      <Icon className={`w-6 h-6 ${accent ? "text-[#FF4D00]" : "text-[#FF4D00]"}`} />
      <div className={`mono text-[10px] uppercase tracking-widest mt-3 ${accent ? "text-zinc-400" : "text-zinc-500"}`}>
        {label}
      </div>
      <div className="display text-3xl mt-1">{value}</div>
    </div>
  );
}
